import React, { useState, useEffect } from 'react';
import { TextField, Select } from '@anyemp/ui-kit';
import ModalPortal from '../UI/ModalPortal';
import ModalFrame from '../UI/ModalFrame';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { X, Eye, EyeOff, Loader } from 'lucide-react';
import { accounts, libraries } from '../../services/apiService';
import toast from 'react-hot-toast';

const CreateSocialAccountModal = ({ isOpen, onClose, accountId }) => {
  const queryClient = useQueryClient();
  const [showPassword, setShowPassword] = useState(false);

  const { data: toolsData } = useQuery({
    queryKey: ['tools', 'Social network'],
    queryFn: () => libraries.getTools({ toolTypeNames: 'Social network' }),
    enabled: isOpen,
  });

  const tools = toolsData?.data || [];

  const [formData, setFormData] = useState({
    tool_id: '',
    username: '',
    link: '',
    login: '',
    password: '',
  });

  useEffect(() => {
    if (!isOpen) return;
    if (tools.length > 0 && !formData.tool_id) {
      const linkedin = tools.find(t => t.name?.toLowerCase() === 'linkedin');
      if (linkedin) {
        setFormData(prev => ({ ...prev, tool_id: String(linkedin.id) }));
      }
    }
  }, [tools, isOpen]);

  const resetForm = () => {
    setFormData({ tool_id: '', username: '', link: '', login: '', password: '' });
  };

  const createMutation = useMutation({
    mutationFn: () =>
      accounts.createSocialAccountForOwnedAccount(accountId, {
        ...formData,
        tool_id: Number(formData.tool_id),
      }),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Social account created successfully!');
        queryClient.invalidateQueries(['myAccounts']);
        onClose();
        resetForm();
      } else {
        toast.error(response.error || 'Failed to create social account');
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create social account');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.tool_id) {
      toast.error('Platform is required');
      return;
    }

    if (!formData.login) {
      toast.error('Login is required');
      return;
    }

    if (!formData.password) {
      toast.error('Password is required');
      return;
    }

    createMutation.mutate();
  };

  if (!isOpen) return null;

  return (
    <ModalPortal onClose={onClose} ariaLabel="Create social account">
      <ModalFrame size="md" className="-m-3 w-[calc(100%+1.5rem)] rounded-none dark:bg-dark-bg-primary sm:m-0 sm:w-full sm:rounded-lg">
        <form onSubmit={handleSubmit} className="contents">
        <ModalFrame.Header className="items-center">
          <h2 className="text-xl font-semibold text-light-text-primary dark:text-dark-text-primary">
            Create Social Account
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-bg-secondary hover:text-light-text-primary dark:hover:bg-dark-bg-secondary dark:hover:text-dark-text-primary"
          >
            <X className="h-5 w-5" />
          </button>
        </ModalFrame.Header>

        <ModalFrame.Body className="space-y-4">
          <Select
            label="Platform"
            required
            fullWidth
            placeholder="Select platform..."
            value={formData.tool_id ? String(formData.tool_id) : ''}
            onChange={(v) => setFormData({ ...formData, tool_id: v || '' })}
            options={tools.map((tool) => ({ value: String(tool.id), label: tool.name }))}
          />

          <div>
            <TextField
              label="Username"
              fullWidth
              type="text"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
            />
          </div>

          <div>
            <TextField
              label="Link"
              fullWidth
              type="url"
              value={formData.link}
              onChange={(e) => setFormData({ ...formData, link: e.target.value })}
            />
          </div>

          <div>
            <TextField
              label="Login"
              required
              fullWidth
              type="text"
              value={formData.login}
              onChange={(e) => setFormData({ ...formData, login: e.target.value })}
            />
          </div>

          <div>
            <TextField
              label="Password"
              required
              fullWidth
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              endAdornment={(
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-md text-light-text-secondary hover:bg-light-bg-secondary dark:text-dark-text-secondary dark:hover:bg-dark-bg-secondary"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              )}
            />
          </div>

        </ModalFrame.Body>
        <ModalFrame.Footer className="flex flex-col-reverse gap-3 sm:flex-row sm:flex-wrap sm:justify-end [&>button]:w-full sm:[&>button]:w-auto">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-light-border-primary dark:border-dark-border-primary rounded-lg text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="flex items-center space-x-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white rounded-lg transition-colors"
            >
              {createMutation.isPending && <Loader className="h-4 w-4 animate-spin" />}
              <span>{createMutation.isPending ? 'Creating...' : 'Create'}</span>
            </button>
        </ModalFrame.Footer>
        </form>
      </ModalFrame>
    </ModalPortal>
  );
};

export default CreateSocialAccountModal;
